import { Auth } from "aws-amplify";
import { getBaseEndpoint } from "../app/utils";
import { getClientMetadataHeaders } from "./client-metadata";

/**
 * Minimal fetch-based client for simpl-api's REST routes.
 *
 * Designed to replace Amplify's `API.graphql` / `API.get` / `API.post` /
 * `API.put` / `API.del`. Goals:
 *   - `credentials: "include"` so the SPA ships the `SIMPL_AUTH_JWT_<env>`
 *     cookie cross-origin once the bootstrap bridge has minted a session.
 *   - `Authorization: Bearer <cognito_id_token>` fallback while Amplify is
 *     still managing the login session. The simpl-auth-authorizer Lambda
 *     accepts tokens from both issuers during the rollout window, so this
 *     is entirely non-blocking and will be removed once the bridge lands
 *     and every session is simpl_auth-signed. The bootstrap probe in
 *     `features/Auth/simplAuthSession.ts` opts out via
 *     `skipCognitoBearer: true` — see that option's docstring below for
 *     why the probe alone has to insist on cookie-only auth.
 *   - `X-Client-*` metadata headers on every request, matching what the
 *     Amplify `custom_header` callback used to produce.
 */

export type QueryStringParameters = Record<
  string,
  string | number | boolean | undefined | null
>;

export type ApiRequestOptions = {
  queryStringParameters?: QueryStringParameters;
  body?: unknown;
  headers?: Record<string, string>;
  /** Test seam; overrides `getBaseEndpoint()`. */
  baseEndpoint?: string;
  /**
   * Suppresses the `Authorization: Bearer <cognito_id_token>` fallback for
   * this request. Use only on requests whose entire purpose is to detect
   * whether the client has a `simpl_auth` cookie yet — i.e. the bootstrap
   * probe in `features/Auth/simplAuthSession.ts#probeSimplAuthSession`.
   *
   * Without this opt-out, every probe carrying a live Amplify/Cognito
   * session would be silently authed by the dual-issuer authorizer, the
   * probe would succeed, and the `<SimplAuthGuard>` would skip the
   * cognito-exchange bridge entirely — meaning existing users would
   * never get migrated to the cookie-based session and would never hit
   * the bridge's MFA-enrollment gate. The probe insists on cookie-only
   * auth; every other request keeps the dual-issuer fallback for the
   * duration of the §6.7 sunset window.
   */
  skipCognitoBearer?: boolean;
};

export type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

export class SimplApiHttpError extends Error {
  readonly status: number;
  readonly response: unknown;

  constructor(message: string, status: number, response: unknown) {
    super(message);
    this.name = "SimplApiHttpError";
    this.status = status;
    this.response = response;
  }
}

async function getCognitoBearerToken(): Promise<string | null> {
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch {
    // No authenticated Cognito session — unauthed endpoints (invite
    // verify / complete) still work; authed endpoints will 401 at the
    // authorizer.
    return null;
  }
}

function buildQueryString(params?: QueryStringParameters): string {
  if (!params) return "";
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    qs.append(key, String(value));
  }
  const serialized = qs.toString();
  return serialized ? `?${serialized}` : "";
}

function resolveFullUrl(base: string, path: string, qs: string): string {
  const trimmedBase = base.endsWith("/") ? base.slice(0, -1) : base;
  let cleanPath = path.startsWith("/") ? path : `/${path}`;

  // Operation mappings use paths like `/api/users`. Local base is
  // `http://localhost:3001/api/`, cloud base is
  // `https://api[-env].simpl.org/` (no `/api`). Strip any leading `/api`
  // from the path — it's either redundant with the local base or absent
  // in cloud, so dropping it here is correct in both cases.
  if (cleanPath === "/api" || cleanPath.startsWith("/api/")) {
    cleanPath = cleanPath.replace(/^\/api/, "") || "/";
  }

  return `${trimmedBase}${cleanPath}${qs}`;
}

export async function simplApiRequest<T = unknown>(
  method: HttpMethod,
  path: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const base = options.baseEndpoint || getBaseEndpoint();
  const qs = buildQueryString(options.queryStringParameters);
  const url = resolveFullUrl(base, path, qs);

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...getClientMetadataHeaders(),
    ...(options.headers || {})
  };
  const sendsBody = method !== "GET" && options.body !== undefined;
  if (sendsBody && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  if (!options.skipCognitoBearer) {
    const bearer = await getCognitoBearerToken();
    if (bearer && !headers["Authorization"]) {
      headers["Authorization"] = `Bearer ${bearer}`;
    }
  }

  const init: RequestInit = {
    method,
    headers,
    // Required so the SPA forwards the HttpOnly SIMPL_AUTH_JWT_<env>
    // cookie cross-origin to the API Gateway domain. Works today even
    // when no cookie is set; simply a no-op in that case.
    credentials: "include"
  };
  if (sendsBody) {
    init.body =
      typeof options.body === "string"
        ? options.body
        : JSON.stringify(options.body);
  }

  const response = await fetch(url, init);
  const rawText = await response.text().catch(() => "");
  let parsed: unknown = undefined;
  if (rawText.length > 0) {
    try {
      parsed = JSON.parse(rawText);
    } catch {
      parsed = rawText;
    }
  }

  if (!response.ok) {
    const parsedObj =
      typeof parsed === "object" && parsed !== null
        ? (parsed as Record<string, unknown>)
        : undefined;
    const innerError =
      parsedObj && typeof parsedObj.error === "string"
        ? (parsedObj.error as string)
        : undefined;
    const innerMessage =
      parsedObj && typeof parsedObj.message === "string"
        ? (parsedObj.message as string)
        : undefined;
    // Prefer a specific upstream message over the generic "Lambda function
    // error" wrapper that API Gateway returns for 5xxs.
    let detail = "";
    if (innerMessage && innerError === "Lambda function error") {
      detail = `: ${innerMessage}`;
    } else if (innerError) {
      detail = `: ${innerError}`;
    } else if (innerMessage) {
      detail = `: ${innerMessage}`;
    } else if (typeof parsed === "string" && parsed.length < 200) {
      detail = `: ${parsed}`;
    }
    throw new SimplApiHttpError(
      `HTTP ${response.status}: ${response.statusText}${detail}`,
      response.status,
      parsed
    );
  }

  return parsed as T;
}
